/*=========================================================================================
  File Name: main.js
  Description: main vue(js) file

==========================================================================================*/


import Vue from 'vue'
import App from './App.vue'
// Vuesax Component Framework
import Vuesax from 'vuesax'
import 'material-icons/iconfont/material-icons.css' //Material Icons
import 'vuesax/dist/vuesax.css' // Vuesax
// Vue validation
import {extend, ValidationObserver, ValidationProvider} from 'vee-validate'
import {email, max, min, required} from 'vee-validate/dist/rules'
// axios
import axios from './axios.js'
// Filters
import './filters/filters.js'
// Theme Configurations
import '../themeConfig.js'
// Globally Registered Components
import './globalComponents.js'
// Styles: SCSS
import './assets/scss/main.scss'
// Tailwind
import '@/assets/css/main.css'
// Vue Router
import router from './router'
// Vuex Store
import store from './store/store'
// Vuejs - Vue wrapper for hammerjs
import {VueHammer} from 'vue2-hammer'
// Vue Laravel acl
import laravel_acl from './plugins/laravel-acl'
// PrismJS
import 'prismjs'
import 'prismjs/themes/prism-tomorrow.css'
import moment from 'moment';
import VueFormulate from '@braid/vue-formulate'

// VUE IDLE
import IdleVue from 'idle-vue';
import VueNativeNotification from 'vue-native-notification'
// Google Maps
import * as VueGoogleMaps from 'vue2-google-maps'

const eventsHub = new Vue()

Vue.use(Vuesax)
Vue.use(VueFormulate, {
  uploader: axios,
  uploadUrl: '/admin/supplier/file-upload'
})

Vue.use(VueGoogleMaps, {
  load: {
    // Add your API key here
    key: 'AIzaSyC4wknUBpBv4JfmLFSd-XO9mzw3ClL3gUk',
    libraries: 'places' // This is required if you use the Auto complete plug-in
  }
})

// Add a rule.
extend('required', {
  ...required,
  message: 'This field is required'
})
extend('file_required', {
  ...required,
  message: 'Please upload a file'
})
extend('min', {
  ...min,
  message: 'This field must have no less than {length} characters'
})
extend('max', {
  ...max,
  message: 'This field must have no more than {length} characters'
})

extend('tel', {
  validate: value => value.match(/^[0-9]*$/u),
  message: 'Telephone Number must not contain any letter or special character'
})

extend('four_decimal', {
  validate: value => value.match(/^[0-9]*(?:\.[0-9]{0,4})?$/u),
  message: 'Quantity must be a numeric with upto four decimal places'
})

extend('number', {
  validate: value => value.match(/^[1-9]\d*(\.\d+)?$/u),
  message: 'Value must be a valid number'
})

extend('two_decimal', {
  validate: value => value.match(/^[0-9]*\.[0-9]{2}$/u),
  message: 'Value must be a numeric with two decimal places'
})

extend('bank_sort_code', {
  validate: value => value.match(/^[0-9][0-9][-][0-9][0-9][-][0-9][0-9]$/u),
  message: 'Format should be XX-XX-XX'
})

extend('flash_point', {
  validate: value => value.match(/^[1-9]*(?:\.[0-9]{0,1})?$/u),
  message: 'Flash Point has to be greater than 0 and have 1 decimal places'
})

extend('email', {
  ...email,
  message: 'Please enter valid email address'
})

extend('no_whitespace', {
  validate: value => value.match(/^\S*$/u),
  message: 'Password must not contain whitespace'
})
extend('min_tel', {
  validate: value => value.length > 6,
  message: 'Contact number must contain at least 7 numbers.'
})


extend('fax', {
  validate: value => value.match(/^[0-9]*$/u),
  message: 'Fax No must not contain any letter or special character'
})

extend('max_tel', {
  validate: value => value.length < 17,
  message: 'Contact number allowed only 16 numbers '
})
extend('contactNumber', {
  validate: value => value.match(/^[+]{0,1}[0-9]*$/g),
  message: 'Enter valid contact number '
})


extend('g2fa', {
  validate: value => value.length  === 6,
  message: 'Login code must include 6 numbers '
})

extend('one_uppercase', {
  validate: value => value.match(/[a-z]/),
  message: 'Password must contain at least one Lowercase character'
})
extend('one_lowercase', {
  validate: value => value.match(/[A-Z]/),
  message: 'Password must contain at least one Uppercase character'
})
extend('selected', {
  ...required,
  message: 'Select at least one clinic.'
})

extend('one_number', {
  validate: value => value.match(/[0-9]/),
  message: 'Password must contain at least one numeral'
})

extend('one_special', {
  validate: value => value.match(/[$&+,:;=?@#|'<>.^*()%!-]/),
  message: 'Password must contain at least one special character'
})

extend('positive', {
  validate: value => Number(value) > -1,
  message: 'You cannot have a negative number.'
})

extend('same_password', {
  params: ['target'],
  validate (value, { target }) {
    return value === target
  },
  message: 'Password confirmation should be matched'
})

extend('moq', {
  params: ['target'],
  validate (value, { target }) {
    return Number(value) >= Number(target)
  },
  message(value, { target }) {
    return `The MOQ is ${target}`
  }
})

extend('maxoq', {
  params: ['target'],
  validate (value, { target }) {
    return Number(value) <= Number(target)
  },
  message(value, { target }) {
    return `The MAXOQ is ${target}`
  }
})

extend('min_than', {
  params: ['target'],
  validate (value, { target }) {
    if (!!target) {
      return Number(value) < Number(target)
    } else {
      return true
    }
  },
  message: `The MOQ must be smaller than the MaxOQ`
})
extend('max_than', {
  params: ['target'],
  validate (value, { target }) {
    if (!!target) {
      return Number(value) > Number(target)
    } else {
      return true
    }
  },
  message: `The MOQ must not be larger than MaxOQ`
})

extend('cross_over_max', {
  params: ['target'],
  validate (value, { target }) {
    if (!!target) {
      return Number(value) > Number(target)
    } else {
      return true
    }
  },
  message: `The MOQ must be larger than previous MaxOQ`
})

extend('min_qty', {
  params: ['target'],
  validate (value, { target }) {
    return Number(value) <= Number(target)
  },
  message(value, { target }) {
    return `The value should be less than or equal to ${target}`
  }
})


// Register it globally
Vue.component('ValidationObserver', ValidationObserver)
Vue.component('ValidationProvider', ValidationProvider)

Vue.mixin({
  methods: {
    __dateTimeFormatter:function(dateStr){
      if(dateStr){
        // return moment(Date).format("DD-MMM-YYYY HH:MM")
        return moment.utc(dateStr).local().format('DD/MM/YYYY HH:mm:ss')
      }
      else{
        return ""
      }
    },
    __dateTimeFormatterToDate:function(dateStr){
      if(dateStr){
        // return moment(Date).format("DD-MMM-YYYY HH:MM")
        return moment.utc(dateStr).local().format('DD/MM/YYYY')
      }
      else{
        return ""
      }
    },
    __utcToLocalTime(time){
      if(time) {
        return moment.utc(time, 'HH:mm').local().format('HH:mm')
      } else {
        return "00:00"
      }
    },
    __nameSplitter: function(link) {
      if (link) {
        let fields = link.split('/');
        const file_full_name = fields[fields.length - 1]
        let second_field =  file_full_name.split('-').slice(1).join('-')
        return second_field.replaceAll('%20', ' ')
      } else {
        return 'No file uploaded'
      }
    },
    __isCurrencyGbp(currency) {
      return currency === 'GBP'
    }
  }
})


Vue.prototype.$http = axios

// axios.interceptors.request.use(
//   response => {
//     return Promise.resolve(response);
//   },
//   error => {
//     return Promise.reject(error);
//   }
// );


Vue.use(VueHammer)

Vue.use(laravel_acl, router)

Vue.use(IdleVue, {
  eventEmitter: eventsHub,
  store,
  idleTime: 3600000,
  startAtIdle: false
});

Vue.use(VueNativeNotification, {
  requestOnNotify: true
})

// Feather font icon
require('./assets/css/iconfont.css')


// Vue select css
// Note: In latest version you have to add it separately
// import 'vue-select/dist/vue-select.css';


Vue.config.productionTip = false

new Vue({
  router,
  store,
  render: h => h(App)
}).$mount('#app')
